"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { RECYCLER_CERTIFICATION_PATH, type RecyclerGateState } from "@convex/constants/recyclerCertification.lib";
import { AuthDialog } from "@/components/auth/auth-dialog";
import { ShieldCheck } from "@/components/icons";
import { Button } from "@/components/ui/button";

type ClosedGate = Exclude<RecyclerGateState, "open">;

const DETAIL: Record<ClosedGate, string> = {
  sign_in: "This load held hazardous materials, so it ships only to certified recyclers. Sign in, send your W-9 and company documents, and order once you're approved.",
  apply: "This load held hazardous materials, so it ships only to certified recyclers. Send your W-9 and company documents, and order once you're approved.",
  in_review: "Your application is in review. We'll email you the decision, and you can order as soon as you're approved.",
  declined: "Your last application wasn't approved. See what to fix and apply again.",
};

function SignInToApply({ href }: { href: string }) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="primary" className="w-full" onClick={() => setOpen(true)}>
        Sign in to apply
      </Button>
      <AuthDialog
        open={open}
        onOpenChange={setOpen}
        entryPoint="listing_recycler_only"
        reason="Sign in to apply as a certified recycler."
        onSignedIn={() => {
          setOpen(false);
          router.push(href);
        }}
      />
    </>
  );
}

function GateAction({ gate, href }: { gate: ClosedGate; href: string }) {
  if (gate === "sign_in") return <SignInToApply href={href} />;
  const label = gate === "in_review" ? "View your application" : gate === "declined" ? "See why and apply again" : "Apply to become certified";
  return (
    <Button variant={gate === "in_review" ? "outline" : "primary"} asChild className="w-full">
      <Link href={href}>{label}</Link>
    </Button>
  );
}

export function ListingRecyclerOnlyCard({ gate, listingSlug }: { gate: ClosedGate; listingSlug?: string }) {
  const href = listingSlug ? `${RECYCLER_CERTIFICATION_PATH}?listing=${encodeURIComponent(listingSlug)}` : RECYCLER_CERTIFICATION_PATH;
  return (
    <section aria-label="Certified recyclers only" className="space-y-4">
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden />
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">Certified recyclers only</h3>
          <p className="text-sm leading-relaxed text-muted-foreground">{DETAIL[gate]}</p>
        </div>
      </div>
      <GateAction gate={gate} href={href} />
    </section>
  );
}
